import { jsPDF } from "jspdf";
import FONTS from "./fonts.js";

const FONT_NAME = "Roboto";

function ensureFontsRegistered(doc) {
  // Register subsetted Roboto (regular/bold/italic) on this jsPDF doc instance.
  // jsPDF needs each variant added separately to its VFS + font map.
  doc.addFileToVFS("Roboto-Regular.ttf", FONTS.normal);
  doc.addFont("Roboto-Regular.ttf", FONT_NAME, "normal");
  doc.addFileToVFS("Roboto-Bold.ttf", FONTS.bold);
  doc.addFont("Roboto-Bold.ttf", FONT_NAME, "bold");
  doc.addFileToVFS("Roboto-Italic.ttf", FONTS.italic);
  doc.addFont("Roboto-Italic.ttf", FONT_NAME, "italic");
}

// A4: 210mm x 297mm, margins: 15mm left/right, 12mm top/bottom
const PAGE_W = 210;
const PAGE_H = 297;
const ML = 15; // margin left
const MR = 15; // margin right
const MT = 12; // margin top
const MB = 12; // margin bottom
const CW = PAGE_W - ML - MR; // content width

function fmtDate(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}/${m}/${date.getFullYear()}`;
}

function fmtDateFromISO(iso) {
  if (!iso) return "___________";
  const [y, mo, d] = iso.split("-");
  return `${d}/${mo}/${y}`;
}

function formatCI(ci) {
  if (!ci) return "___ ___";
  const clean = ci.replace(/\s+/g, "");
  if (clean.length >= 8) return clean.substring(0, 2) + " " + clean.substring(2);
  return ci;
}

export { fmtDate, fmtDateFromISO, formatCI };

export default class PDFBuilder {
  constructor() {
    this.doc = new jsPDF({ unit: "mm", format: "a4" });
    ensureFontsRegistered(this.doc);
    this.y = MT;
    this.pageNum = 1;
  }

  // Check if we need a new page, with optional space reservation
  checkPage(needed = 8) {
    if (this.y + needed > PAGE_H - MB) {
      this.doc.addPage();
      this.y = MT;
      this.pageNum++;
    }
  }

  // Set font helper
  setFont(style = "normal", size = 10) {
    // Roboto subset only ships normal/bold/italic — there's no "bolditalic" cut,
    // so requests for it fall back to bold (closest substitute).
    const resolved = style === "bolditalic" ? "bold" : style;
    this.doc.setFont(FONT_NAME, resolved);
    this.doc.setFontSize(size);
  }

  // Write wrapped text and advance y. Returns lines used.
  writeText(text, { x = ML, maxWidth = CW, size = 10, style = "normal", color = [61, 50, 37], align = "left", lineHeight = 1.5 } = {}) {
    this.setFont(style, size);
    this.doc.setTextColor(...color);
    const lines = this.doc.splitTextToSize(text, maxWidth);
    const lh = size * 0.3528 * lineHeight; // pt to mm * lineHeight
    for (let i = 0; i < lines.length; i++) {
      this.checkPage(lh);
      if (align === "center") {
        this.doc.text(lines[i], PAGE_W / 2, this.y, { align: "center" });
      } else if (align === "right") {
        this.doc.text(lines[i], x + maxWidth, this.y, { align: "right" });
      } else {
        this.doc.text(lines[i], x, this.y);
      }
      this.y += lh;
    }
    return lines.length;
  }

  // Write text with inline bold segments: "normal {bold text} normal"
  // Segments are: [{text, bold}]
  writeMixed(segments, { x = ML, maxWidth = CW, size = 10, color = [61, 50, 37], lineHeight = 1.5 } = {}) {
    this.doc.setFontSize(size);
    this.doc.setTextColor(...color);
    const lh = size * 0.3528 * lineHeight;

    // Build a flat list of tokens (words/spaces) with their font style
    const tokens = [];
    for (const seg of segments) {
      if (!seg.text) continue;
      // Split into words and whitespace, preserving order
      const parts = seg.text.match(/\S+|\s+/g) || [];
      for (const part of parts) {
        tokens.push({ text: part, bold: !!seg.bold });
      }
    }

    // Measure each token with its actual font
    for (const token of tokens) {
      this.setFont(token.bold ? "bold" : "normal", size);
      token.width = this.doc.getTextWidth(token.text);
    }

    // Greedy line wrapping
    const lines = [];
    let currentLine = [];
    let currentWidth = 0;

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      const isSpace = /^\s+$/.test(token.text);

      if (currentLine.length === 0) {
        // Skip leading spaces on a new line
        if (isSpace) continue;
        currentLine.push(token);
        currentWidth = token.width;
      } else if (currentWidth + token.width <= maxWidth) {
        currentLine.push(token);
        currentWidth += token.width;
      } else {
        // This token overflows — start a new line
        // Remove trailing whitespace from current line
        while (currentLine.length > 0 && /^\s+$/.test(currentLine[currentLine.length - 1].text)) {
          currentLine.pop();
        }
        lines.push(currentLine);
        currentLine = [];
        currentWidth = 0;
        // Skip spaces at line break, add word tokens
        if (!isSpace) {
          currentLine.push(token);
          currentWidth = token.width;
        }
      }
    }
    // Push final line (remove trailing whitespace)
    if (currentLine.length > 0) {
      while (currentLine.length > 0 && /^\s+$/.test(currentLine[currentLine.length - 1].text)) {
        currentLine.pop();
      }
      if (currentLine.length > 0) lines.push(currentLine);
    }

    // Render each line
    for (const lineTokens of lines) {
      this.checkPage(lh);
      let cx = x;
      for (const token of lineTokens) {
        this.setFont(token.bold ? "bold" : "normal", size);
        this.doc.text(token.text, cx, this.y);
        cx += token.width;
      }
      this.y += lh;
    }
  }

  // Contract title (centered, large)
  title(text, subtitle) {
    this.writeText(text, { size: 16, style: "bold", color: [45, 36, 24], align: "center", lineHeight: 1.3 });
    if (subtitle) {
      this.space(1);
      this.writeText(subtitle, { size: 10, style: "normal", color: [138, 126, 112], align: "center" });
    }
    this.space(3);
    this.line();
    this.space(4);
  }

  // Section heading (e.g. "I. PARTILE CONTRACTANTE")
  section(text) {
    this.checkPage(14);
    this.space(4);
    this.writeText(text, { size: 11, style: "bold", color: [45, 36, 24], lineHeight: 1.3 });
    this.space(1);
    // Thin line under heading
    this.doc.setDrawColor(232, 224, 212);
    this.doc.setLineWidth(0.3);
    this.doc.line(ML, this.y, PAGE_W - MR, this.y);
    this.space(3);
  }

  // Regular paragraph
  p(text, { indent = 0, size = 10 } = {}) {
    this.writeText(text, { x: ML + indent, maxWidth: CW - indent, size, lineHeight: 1.6 });
    this.space(1.5);
  }

  // Paragraph with mixed bold/normal segments
  pMixed(segments, { indent = 0 } = {}) {
    this.writeMixed(segments, { x: ML + indent, maxWidth: CW - indent, lineHeight: 1.6 });
    this.space(1.5);
  }

  // Numbered article: "2.1. Some text with {bold parts}"
  article(number, text) {
    this.pMixed([
      { text: `${number} `, bold: true },
      { text },
    ]);
  }

  // Numbered article with bold value inline
  articleBold(number, prefix, value, suffix = "") {
    this.pMixed([
      { text: `${number} `, bold: true },
      { text: prefix },
      { text: value, bold: true },
      { text: suffix },
    ]);
  }

  // Lettered list item: "a) text"
  letterItem(letter, text, { indent = 8 } = {}) {
    this.pMixed([
      { text: `${letter}) `, bold: true },
      { text },
    ], { indent });
  }

  // Field row (e.g. "Nume: Popescu Ion")
  field(label, value, { indent = 5 } = {}) {
    this.pMixed([
      { text: `${label}: ` },
      { text: value || "________________________", bold: true },
    ], { indent });
  }

  // Italic centered text (e.g. "si")
  italic(text) {
    this.writeText(text, { style: "italic", color: [138, 126, 112], align: "center" });
    this.space(2);
  }

  // Intro text in italics
  intro(text) {
    this.writeText(text, { style: "italic", lineHeight: 1.6 });
    this.space(2);
  }

  // Horizontal line
  line() {
    this.doc.setDrawColor(232, 224, 212);
    this.doc.setLineWidth(0.5);
    this.doc.line(ML, this.y, PAGE_W - MR, this.y);
    this.space(2);
  }

  // Space in mm
  space(mm) {
    this.y += mm;
  }

  // Signature block with two columns
  signatures(left, right) {
    this.checkPage(40);
    this.space(6);
    this.line();
    this.space(4);

    const colW = CW / 2 - 5;
    const leftX = ML;
    const rightX = ML + CW / 2 + 5;
    const savedY = this.y;

    // Left column
    this.setFont("bold", 9);
    this.doc.setTextColor(138, 126, 112);
    this.doc.text(left.role, leftX + colW / 2, this.y, { align: "center" });
    this.y += 12;
    this.doc.setDrawColor(212, 205, 192);
    this.doc.setLineWidth(0.3);
    this.doc.line(leftX + 8, this.y, leftX + colW - 8, this.y);
    this.y += 3;
    this.setFont("bold", 10);
    this.doc.setTextColor(45, 36, 24);
    const leftNameLines = this.doc.splitTextToSize(left.name || "", colW - 16);
    leftNameLines.forEach((ln) => {
      this.doc.text(ln, leftX + colW / 2, this.y, { align: "center" });
      this.y += 4;
    });
    this.y += 2;
    this.setFont("normal", 9);
    this.doc.setTextColor(138, 126, 112);
    this.doc.text(`Data: ${left.date}`, leftX + colW / 2, this.y, { align: "center" });
    const leftEndY = this.y;

    // Right column
    this.y = savedY;
    this.setFont("bold", 9);
    this.doc.setTextColor(138, 126, 112);
    this.doc.text(right.role, rightX + colW / 2, this.y, { align: "center" });
    this.y += 12;
    this.doc.setDrawColor(212, 205, 192);
    this.doc.setLineWidth(0.3);
    this.doc.line(rightX + 8, this.y, rightX + colW - 8, this.y);
    this.y += 3;
    this.setFont("bold", 10);
    this.doc.setTextColor(45, 36, 24);
    const rightNameLines = this.doc.splitTextToSize(right.name || "", colW - 16);
    rightNameLines.forEach((ln) => {
      this.doc.text(ln, rightX + colW / 2, this.y, { align: "center" });
      this.y += 4;
    });
    this.y += 2;
    this.setFont("normal", 9);
    this.doc.setTextColor(138, 126, 112);
    this.doc.text(`Data: ${right.date}`, rightX + colW / 2, this.y, { align: "center" });
    const rightEndY = this.y;

    this.y = Math.max(leftEndY, rightEndY) + 8;
  }

  // Single centered signature block
  signatureSingle(sig) {
    this.checkPage(35);
    this.space(4);

    const centerX = PAGE_W / 2;

    this.setFont("bold", 9);
    this.doc.setTextColor(138, 126, 112);
    this.doc.text(sig.role, centerX, this.y, { align: "center" });
    this.y += 12;
    this.doc.setDrawColor(212, 205, 192);
    this.doc.setLineWidth(0.3);
    this.doc.line(centerX - 30, this.y, centerX + 30, this.y);
    this.y += 3;
    this.setFont("bold", 10);
    this.doc.setTextColor(45, 36, 24);
    this.doc.text(sig.name || "", centerX, this.y, { align: "center" });
    this.y += 5;
    this.setFont("normal", 9);
    this.doc.setTextColor(138, 126, 112);
    this.doc.text(`Data: ${sig.date}`, centerX, this.y, { align: "center" });
    this.y += 8;
  }

  // Save the PDF
  save(filename) {
    this.doc.save(filename);
  }

  // Render party info (PF/PJ) — used by inchiriere, imprumut, vanzare-auto, comodat
  renderPartyPFPJ(data, prefix, isPJ) {
    if (isPJ) {
      this.field("Denumire", data[prefix + "Firma"]);
      this.field("CUI", data[prefix + "CUI"]);
      if (data[prefix + "RegCom"]) this.field("Nr. Reg. Com.", data[prefix + "RegCom"]);
      this.field("Sediu social", data[prefix + "Sediu"]);
      this.field("Reprezentant legal", data[prefix + "Reprezentant"]);
    } else {
      this.field("Nume", data[prefix + "Nume"]);
      this.field("CNP", data[prefix + "CNP"]);
      this.field("CI seria", formatCI(data[prefix + "CI"]) || "___ ___");
      this.field("Domiciliul", data[prefix + "Adresa"]);
    }
  }

  // Render party info (PF/PFA/SRL) — used by prestari-servicii
  renderPartyTip(data, prefix) {
    const tip = data[prefix + "Tip"];
    if (tip === "pf") {
      this.field("Nume", data[prefix + "Nume"]);
      this.field("CNP", data[prefix + "CNP"]);
      this.field("CI seria", formatCI(data[prefix + "CI"]) || "___ ___");
      this.field("Domiciliul", data[prefix + "Adresa"]);
    } else if (tip === "pfa") {
      this.field("Denumire", data[prefix + "Denumire"]);
      this.field("CUI", data[prefix + "CUI"]);
      this.field("Sediu", data[prefix + "Sediu"]);
      this.field("Reprezentant", data[prefix + "Reprezentant"]);
    } else {
      this.field("Denumire", data[prefix + "Denumire"]);
      this.field("CUI", data[prefix + "CUI"]);
      if (data[prefix + "RegCom"]) this.field("Nr. Reg. Com.", data[prefix + "RegCom"]);
      this.field("Sediu social", data[prefix + "Sediu"]);
      this.field("Reprezentant legal", data[prefix + "Reprezentant"]);
    }
  }

  getPartyNamePFPJ(data, prefix, isPJ) {
    if (isPJ) {
      return data[prefix + "Firma"] ? `${data[prefix + "Firma"]} prin ${data[prefix + "Reprezentant"] || ""}` : "";
    }
    return data[prefix + "Nume"] || "";
  }

  getPartyNameTip(data, prefix) {
    const tip = data[prefix + "Tip"];
    if (tip === "pf") return data[prefix + "Nume"] || "";
    const name = data[prefix + "Denumire"] || "";
    const repr = data[prefix + "Reprezentant"] || "";
    return name ? `${name} prin ${repr}` : "";
  }
}
